import type { TFunction } from 'i18next';
import { formatClock } from '@/features/trips/format';
import type { DriverAbsentAlert } from './types';

/**
 * SLICE D2 — a DRIVER-ABSENT row, the THIRTEENTH item shape of the alerts feed. A
 * vehicle that has a PERMANENT driver whose Raad Driver app is activated moved for
 * ≥ 5 min with NO open shift on it: the "someone drove it and the assigned driver
 * never signed in" case. `severity: 'warning'`, so it counts toward the unread
 * badge — a notch below tamper's `critical`, the same weight as moved-while-parked.
 *
 * 🔴 IT IS A POSITIONED ITEM, always (unlike the driver-UNLINK request, which has
 * no location). `observedLat`/`observedLon` are the fix where the unattended
 * movement was observed and are NON-NULL, so — exactly like `OverspeedRow` /
 * `MovedWhileParkedRow` — it is ALWAYS a select button: selecting centres the
 * fleet map on the observed point (and marks read); on vehicle detail (no map) it
 * just marks read. There is no "last known location" line and no Link fallback.
 *
 * The headline names BOTH the vehicle and the assigned driver — "Demo 01 moved
 * without its driver (Ahmed Cali)" — because the whole point is that THIS named
 * driver was not the one driving. `driverName` degrades to a generic fallback and
 * the plate to a vehicle fallback, so an older/looser wire row still reads sensibly.
 * Tokens only; no hex literal.
 */
export function DriverAbsentRow({
  alert,
  plate,
  locale,
  timeZone,
  selected,
  onSelect,
  t,
}: {
  alert: DriverAbsentAlert;
  plate?: string | null;
  locale: string;
  timeZone: string;
  selected: boolean;
  onSelect: (alert: DriverAbsentAlert) => void;
  t: TFunction;
}) {
  const observedTime = formatClock(locale, Date.parse(alert.observedAt), timeZone);
  const driverName =
    typeof alert.driverName === 'string' && alert.driverName.trim() !== ''
      ? alert.driverName.trim()
      : t('alerts.driverAbsent.driverFallback');
  const vehicle = plate && plate.trim() !== '' ? plate : t('alerts.driverAbsent.vehicleFallback');

  const titleClass = !alert.read ? 'font-semibold text-raad-text' : 'font-medium text-raad-muted';
  const dotClass = alert.read ? 'bg-transparent' : 'bg-raad-warn';

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(alert)}
        aria-pressed={selected}
        data-testid="driver-absent-row"
        data-type={alert.type}
        data-severity={alert.severity}
        data-alert-id={alert.id}
        className={[
          'w-full rounded-xl border px-3.5 py-3 text-left transition-colors',
          selected
            ? 'border-raad-primary/40 bg-raad-primary/10'
            : 'border-raad-line-7 bg-raad-tile hover:border-raad-line-15',
        ].join(' ')}
      >
        <div className="flex items-start gap-2.5">
          <span aria-hidden="true" className={['mt-1.5 size-2 flex-none rounded-full', dotClass].join(' ')} />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className={['truncate text-sm', titleClass].join(' ')}>
                {t('alerts.driverAbsent.headline', { vehicle, driver: driverName })}
              </span>
              <span
                data-testid="driver-absent-tag"
                className="flex-none rounded-md bg-raad-warn/18 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-raad-warn"
              >
                {t('alerts.driverAbsent.tag')}
              </span>
              {!alert.read ? <span className="sr-only">{t('alerts.unread')}</span> : null}
            </div>

            <div className="mt-1 text-[12px] text-raad-dim">
              {t('alerts.observedAt', { time: observedTime })}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}
